import ManualStatsPanel from './ManualStatsPanel';
import React, { useState, useEffect } from 'react';

const C = { grn:'#389e0d', red:'#cf1322', blue:'#0050b3', amber:'#ad4e00', gray:'#8c8c8c' };
const money = v => v == null ? '—' : (v < 0 ? '-' : '') + '\u20B9' + Math.abs(Math.round(v)).toLocaleString('en-IN');
const pnlColor = v => v == null ? 'var(--t2)' : v > 0 ? C.grn : v < 0 ? C.red : 'var(--t2)';

const box = { background:'var(--bg1)', border:'1px solid var(--bd)', borderRadius:8, padding:16, marginBottom:16 };
const th = { textAlign:'left', padding:'6px 10px', fontSize:11, color:'var(--t2)', fontWeight:600,
             textTransform:'uppercase', letterSpacing:0.3, borderBottom:'1px solid var(--bd)', whiteSpace:'nowrap' };
const td = { padding:'6px 10px', fontSize:13, borderBottom:'1px solid var(--bd)', whiteSpace:'nowrap' };

function Table({ cols, rows, render }) {
  if (!rows || !rows.length) return <div style={{ color:'var(--t2)', fontSize:13, padding:8 }}>No data</div>;
  return (
    <div style={{ overflowX:'auto' }}>
      <table style={{ width:'100%', borderCollapse:'collapse' }}>
        <thead><tr>{cols.map(c => <th key={c} style={th}>{c}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i}>{render(r).map((cell, j) =>
          <td key={j} style={td}>{cell}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

function Section({ title, note, children }) {
  return (
    <div style={box}>
      <div style={{ fontSize:14, fontWeight:600, marginBottom: note ? 2 : 10 }}>{title}</div>
      {note && <div style={{ fontSize:11, color:'var(--t2)', marginBottom:10 }}>{note}</div>}
      {children}
    </div>
  );
}

const StatsPanel = () => {
  const [src, setSrc] = useState('FRAMEWORK');
  const [days, setDays] = useState('');
  const [d, setD] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const qs = (withSrc) => {
      const p = [];
      if (days) p.push('days=' + days);
      if (withSrc !== false && src !== 'ALL') p.push('source=' + src);
      return p.length ? '?' + p.join('&') : '';
    };
    const get = async (path, withSrc = true) => {
      try {
        const r = await fetch('/stats/' + path + qs(withSrc));
        return r.ok ? await r.json() : null;
      } catch (e) { return null; }
    };
    const [ov, ex, mfe, cv, ld, hr, ht, eq, ls] = await Promise.all([
      get('overview', false), get('exit-reasons'), get('mfe-bands'),
      get('conviction', false), get('ladder'), get('hourly'),
      get('hold-time'), get('equity-curve'), get('losers')
    ]);
    setD({ ov, ex, mfe, cv, ld, hr, ht, eq, ls });
    setLoading(false);
  };

  useEffect(() => { load(); }, [src, days]);

  const filt = rows => !rows ? [] : (src === 'ALL' ? rows : rows.filter(r => !r.source || r.source === src));
  const ovRows = (d.ov && d.ov.rows) || [];
  const eq = d.eq || {};

  const btn = (active, color) => ({
    padding:'5px 14px', fontSize:12, cursor:'pointer', borderRadius:6,
    border:'1px solid ' + (active ? color : 'var(--bd)'),
    background: active ? color : 'transparent',
    color: active ? '#fff' : 'var(--t2)', fontWeight: active ? 600 : 500, marginRight:6
  });

  if (src === 'MANUAL') {
    return (
      <div style={{ padding:16 }}>
        <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:4, flexWrap:'wrap' }}>
          <div style={{ fontSize:18, fontWeight:700 }}>Statistics</div>
          <div>
            {['MANUAL','FRAMEWORK','LEGACY','ALL'].map(x =>
              <button key={x} onClick={() => setSrc(x)} style={btn(src === x, C.blue)}>
                {x === 'MANUAL' ? 'MANUAL TRADES' : x}</button>)}
          </div>
        </div>
        <ManualStatsPanel />
      </div>
    );
  }

  return (
    <>
    <div style={{ padding:16 }}>
      <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:16, flexWrap:'wrap' }}>
        <div style={{ fontSize:18, fontWeight:700 }}>SwingScan Statistics</div>
        <div>
          {['MANUAL','FRAMEWORK','LEGACY','ALL'].map(s =>
            <button key={s} onClick={() => setSrc(s)} style={btn(src === s, C.blue)}>{s === 'MANUAL' ? 'MANUAL TRADES' : s}</button>)}
        </div>
        <div>
          {[['All time',''],['30d','30'],['7d','7']].map(x =>
            <button key={x[1]} onClick={() => setDays(x[1])} style={btn(days === x[1], C.amber)}>{x[0]}</button>)}
        </div>
        <button onClick={load} style={Object.assign({}, btn(false, C.gray), { marginLeft:'auto' })}>Refresh</button>
      </div>

      {loading && <div style={{ color:'var(--t2)', fontSize:13 }}>Loading...</div>}

      <Section title="Overview" note="FRAMEWORK = intelligence-scored entries (live since 27 Jul). LEGACY = pre-framework Path A/B/C. Never read them as one number.">
        <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
          {ovRows.map(r => (
            <div key={r.source} style={{ flex:'1 1 300px', border:'1px solid var(--bd)', borderRadius:8, padding:12 }}>
              <div style={{ fontSize:12, fontWeight:700, color: r.source === 'FRAMEWORK' ? C.blue : C.gray, marginBottom:8 }}>
                {r.source}
              </div>
              <div style={{ fontSize:24, fontWeight:700, color:pnlColor(r.net_pnl) }}>{money(r.net_pnl)}</div>
              <div style={{ fontSize:11, color:'var(--t2)', marginBottom:8 }}>
                {r.trades} trades · {r.win_rate}% WR · avg {money(r.avg_pnl)}
              </div>
              <div style={{ fontSize:11, color:'var(--t2)', lineHeight:1.7 }}>
                Gross {money(r.gross_pnl)} · Costs {money(r.costs)}<br/>
                Avg win {money(r.avg_win)} · Avg loss {money(r.avg_loss)}<br/>
                Best {money(r.best)} · Worst {money(r.worst)}<br/>
                Avg MFE {r.avg_mfe == null ? '—' : r.avg_mfe}%
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Equity Curve" note={'Final ' + money(eq.final) + ' · Max drawdown ' + money(eq.max_drawdown)}>
        <Table cols={['Date','Trades','Day P&L','Cumulative','Drawdown']}
          rows={(eq.rows || []).slice(-25).reverse()}
          render={r => [r.d, r.trades,
            <span style={{ color:pnlColor(r.day_pnl) }}>{money(r.day_pnl)}</span>,
            <span style={{ color:pnlColor(r.cumulative) }}>{money(r.cumulative)}</span>,
            <span style={{ color: r.drawdown < 0 ? C.red : 'var(--t2)' }}>{money(r.drawdown)}</span>]} />
      </Section>

      <Section title="MFE Bands" note="How far a trade ran before closing. Trades that never move are where the money leaks.">
        <Table cols={['Source','MFE band','Trades','Total','Avg','Win rate']}
          rows={filt(d.mfe && d.mfe.rows)}
          render={r => [r.source, r.mfe_band, r.n,
            <span style={{ color:pnlColor(r.total), fontWeight:600 }}>{money(r.total)}</span>,
            <span style={{ color:pnlColor(r.avg_pnl) }}>{money(r.avg_pnl)}</span>,
            r.win_rate + '%']} />
      </Section>

      <Section title="Exit Reasons" note="Which exits earn and which bleed.">
        <Table cols={['Source','Exit','Trades','Total','Avg','Win rate','Avg MFE']}
          rows={filt(d.ex && d.ex.rows)}
          render={r => [r.source, r.exit_reason, r.n,
            <span style={{ color:pnlColor(r.total), fontWeight:600 }}>{money(r.total)}</span>,
            <span style={{ color:pnlColor(r.avg_pnl) }}>{money(r.avg_pnl)}</span>,
            r.win_rate + '%', (r.avg_mfe == null ? '—' : r.avg_mfe) + '%']} />
      </Section>

      <Section title="Conviction Bands" note="Framework only. Tests whether net_conviction actually predicts P&L.">
        <Table cols={['Conviction','Trades','Total','Avg','Win rate','Avg MFE']}
          rows={d.cv && d.cv.rows}
          render={r => [r.conviction_band, r.n,
            <span style={{ color:pnlColor(r.total), fontWeight:600 }}>{money(r.total)}</span>,
            <span style={{ color:pnlColor(r.avg_pnl) }}>{money(r.avg_pnl)}</span>,
            r.win_rate + '%', (r.avg_mfe == null ? '—' : r.avg_mfe) + '%']} />
      </Section>

      <Section title="Ladder Tiers">
        <Table cols={['Tier','Trades','Total','Avg','Win rate']}
          rows={d.ld && d.ld.by_tier}
          render={r => [r.ladder_tier, r.n,
            <span style={{ color:pnlColor(r.total), fontWeight:600 }}>{money(r.total)}</span>,
            <span style={{ color:pnlColor(r.avg_pnl) }}>{money(r.avg_pnl)}</span>,
            r.win_rate + '%']} />
        {d.ld && d.ld.reached_be_lock && d.ld.reached_be_lock[0] && (
          <div style={{ fontSize:12, color:'var(--t2)', marginTop:12, lineHeight:1.8 }}>
            Reached +1% MFE: <b>{d.ld.reached_be_lock[0].reached_1pct}</b> of {d.ld.reached_be_lock[0].total}
            {' '}· still lost after +1%: <b style={{ color:C.red }}>{d.ld.reached_be_lock[0].gave_back_after_1pct}</b><br/>
            Reached +2.5% MFE: <b>{d.ld.reached_be_lock[0].reached_2p5pct}</b>
            {' '}· still lost after +2.5%: <b style={{ color:C.red }}>{d.ld.reached_be_lock[0].gave_back_after_2p5pct}</b>
          </div>
        )}
      </Section>

      <Section title="Hold Time" note="Watch this after MFE_STALL removal — positions should hold longer now.">
        <Table cols={['Held','Trades','Total','Avg','Win rate']}
          rows={d.ht && d.ht.rows}
          render={r => [r.held, r.n,
            <span style={{ color:pnlColor(r.total), fontWeight:600 }}>{money(r.total)}</span>,
            <span style={{ color:pnlColor(r.avg_pnl) }}>{money(r.avg_pnl)}</span>,
            r.win_rate + '%']} />
      </Section>

      <Section title="Entry Hour (IST)" note="Observation only. Design principle: no time-based gates — setup-based only.">
        <Table cols={['Hour','Trades','Total','Avg','Win rate']}
          rows={d.hr && d.hr.rows}
          render={r => [r.hour_ist + ':00', r.n,
            <span style={{ color:pnlColor(r.total), fontWeight:600 }}>{money(r.total)}</span>,
            <span style={{ color:pnlColor(r.avg_pnl) }}>{money(r.avg_pnl)}</span>,
            r.win_rate + '%']} />
      </Section>

      <Section title="Worst Trades" note="The ugly. Check for a shared signature.">
        <Table cols={['Ticker','Conv','Tier','Net P&L','MFE','Exit','Entered']}
          rows={d.ls && d.ls.rows}
          render={r => [<b>{r.ticker}</b>, r.net_conviction == null ? '—' : r.net_conviction,
            r.ladder_tier || '—',
            <span style={{ color:pnlColor(r.net_pnl), fontWeight:600 }}>{money(r.net_pnl)}</span>,
            (r.mfe_pct == null ? '—' : r.mfe_pct) + '%', r.exit_reason, r.entry_ist]} />
      </Section>
    </div>
    </>
  );
};

export default StatsPanel;
